import React, { useEffect, useMemo, useState } from "react";
import { useStyles } from "../../../assets/jss/PraisoTable";
import {
    useTable,
    useSortBy,
    usePagination,
    useExpanded,
    useGlobalFilter,
    useGroupBy,
    useFilters,
} from "react-table";
import {
    CustomTable,
    CustomTableBody,
    CustomTableCell,
    CustomTableHead,
    CustomTableRow,
} from "../CustomTable/CustomTable";
import CustomBox from "../CustomBox/CustomBox";
import CustomIconButton from "../CustomIconButton/CustomIconButton";
import {
    CustomChevronLeftIcon,
    CustomChevronRightIcon,
    CustomExpandLessIcon,
    CustomExpandMoreIcon,
    CustomUnfoldMoreIcon,
} from "../CustomIcon/CustomIcon";
import CustomSelect from "../CustomSelect/CustomSelect";
import CustomMenuItem from "../CustomMenuItem/CustomMenuItem";
import { GlobalFilter } from "./GlobalFilter";
import clsx from "clsx";
import CustomLoader from "../CustomLoader/CustomLoader";
import PraisoTableViewSelector from "./PraisoTableViewSelector";
import {
    getUserData,
    getUserRBAC,
} from "../../../services/LocalStorageService";
import { EntityAccess, ReportedEventStatus, TableView } from "../../../config/app-globals.enum";
import { useHistory } from "react-router";
import groupeOffIcon from "../../../assets/images/groupIcon.svg";
import groupeOnIcon from "../../../assets/images/groupIconOn.svg";
import CustomTypography from "../CustomTypography/CustomTypography";
import { useTranslation } from "react-i18next";
import { useParams, Link } from "react-router-dom";
import GlobalDropDown from "./GlobalDropDown";

function useControlledState(state, { instance }) {
    return React.useMemo(() => {
        if (state.groupBy.length) {
            return {
                ...state,
                hiddenColumns: [...state.hiddenColumns, ...state.groupBy].filter(
                    (d, i, all) => all.indexOf(d) === i
                ),
            };
        }
        return state;
    }, [state]);
}

export const PraisoTable = (props) => {
    const params = useParams();
    const { t } = useTranslation();
    const handleRefreshComponent = props.handleRefreshComponent;
    const {
        tableColumns,
        tableData,
        globalFilterEnabled,
        globalFilterPlaceholder,
        groupByEnabled,
        defaultPageSize,
        pageLengthArray,
        selectedTableSorting,
        selectedTableGrouping,
        selectedTableHiddenCell,
        selectedTableRequiredCell,
        selectedTableFilter,
        isAccordionView,
        renderRowSubComponent,
        tableAccordionProps,
        tableName,
        showMoreOrLessComp,
        tableViewList,
        changeTableView,
        showPagination,
        isRowExpanded,
        isHideTableSelector
    } = props;
    const [getRBAC] = React.useState(getUserRBAC(null, props));
    const userData = getUserData();
    const history = useHistory();
    const classes = useStyles();
    const columns = useMemo(() => tableColumns, [tableColumns]);
    const data = useMemo(() => tableData, [tableData]);
    // const [eventCategory, setEventCategory] = useState(props.eventCategory);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        rows,
        page,
        visibleColumns,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state,
        preGlobalFilteredRows,
        setGlobalFilter,
    } = useTable(
        {
            columns,
            data,
            initialState: {
                pageIndex: 0,
                pageSize: defaultPageSize,
                // sortBy: selectedTableSorting,
                groupBy: selectedTableGrouping,
                hiddenColumns: selectedTableHiddenCell,
                filters: selectedTableFilter,
                showPagination: showPagination,
                requiredColumns: selectedTableRequiredCell
            },
        },
        useFilters,
        useGlobalFilter, // useGlobalFilter!
        useGroupBy,
        useSortBy,
        useExpanded,
        usePagination,
        (hooks) => {
            hooks.useControlledState.push(useControlledState);
            hooks.visibleColumns.push((columns, { instance }) => {
                if (!instance.state.groupBy.length) {
                    return columns;
                }

                const groupByColumns = {
                    id: "expanderGroupBy", // Make sure it has an ID
                    // Build our expander column
                    Header: ({ allColumns, state: { groupBy } }) => {
                        return (
                            <div className={classes.groupedCellWrapper}>
                                {groupBy.map((columnId) => {
                                    const column = allColumns.find((d) => d.id === columnId);
                                    return column ? (
                                        <>
                                            <span
                                                style={{
                                                    marginRight: 16,
                                                }}

                                                {...column.getHeaderProps()}
                                            >
                                                {column.canGroupBy ? (
                                                    // If the column can be grouped, let's add a toggle
                                                    <span
                                                        className={classes.groupByIcon}
                                                        {...column.getGroupByToggleProps()}
                                                    >
                                                        <img
                                                            width={24}
                                                            src={
                                                                column.isGrouped ? groupeOnIcon : groupeOffIcon
                                                            }
                                                        />

                                                    </span>
                                                ) : null}
                                                {column.render("Header")}{" "}
                                                {column.canSort ? (
                                                    <span {...column.getSortByToggleProps()}>
                                                        {column.isSorted ? (
                                                            column.isSortedDesc ? (
                                                                <CustomExpandMoreIcon
                                                                    className={classes.sortArrow}
                                                                />
                                                            ) : (
                                                                <CustomExpandLessIcon
                                                                    className={classes.sortArrow}
                                                                />
                                                            )
                                                        ) : (
                                                            <CustomUnfoldMoreIcon
                                                                className={classes.sortArrow}
                                                            />
                                                        )}
                                                    </span>
                                                ) : null}
                                            </span>
                                        </>
                                    ) : null;
                                })}
                            </div>
                        );
                    },
                    Cell: ({ row }) => {
                        if (row.canExpand) {
                            const groupedCell = row.allCells.find((d) => d.isGrouped);
                            return (
                                <div
                                    className={classes.groupedCellWrapper}
                                    {...row.getToggleRowExpandedProps({
                                        style: {
                                            // We can even use the row.depth property
                                            // and paddingLeft to indicate the depth
                                            // of the row
                                            paddingLeft: `${row.depth * 4}rem`,
                                        },
                                    })}
                                >
                                    {row.isExpanded ? (
                                        <CustomExpandLessIcon className={clsx(classes.sortArrow)} />
                                    ) : (
                                        <CustomExpandMoreIcon className={clsx(classes.sortArrow)} />
                                    )}{" "}
                                    <span className={classes.groupedCellLabel}>
                                        <span className={classes.groupedCellLabelPrimary}>
                                            {groupedCell.column.Header}: {groupedCell.render("Cell")}
                                        </span>
                                        <span className={classes.groupedCellLabelSecondary}>
                                            Grouped items: {row.subRows.length}
                                        </span>
                                    </span>
                                </div>
                            );
                        }

                        return null;
                    },
                    style: {

                    }
                };
                if (instance.state.groupBy.length) {
                    //set group by header style 
                    let groupedColumnWidth = 0;
                    const groupColumns = instance.state.groupBy.map((columnId) => {
                        const column = columns.find((d) => d.id === columnId);
                        if (column && column.style && column.style.header) {
                            if (column.style.header.width) {
                                groupedColumnWidth += column.style.header.width;
                            }
                        }
                    });
                    groupByColumns.style = {
                        header: {
                            width: groupedColumnWidth > 0 ? groupedColumnWidth : null, //150
                        },
                        column: {},
                    };
                }

                const newColumnsPostGroupby = [
                    groupByColumns,
                    ...columns,
                ];
                return newColumnsPostGroupby;
            });
        }
    );

    const { pageIndex, pageSize, expanded } = state;

    function setPageCount() {
        if (showPagination === false) {
            if (tableData.length > 0)
                setPageSize(tableData.length + rows.length)
            else
                setPageSize(-1)
        } else {
            setPageSize(defaultPageSize)
        }
    }

    useEffect(() => {
        setPageCount();
    }, [showPagination, defaultPageSize, tableData, rows]);

    const isClickable = (link, rowData) => {
        let response = false;
        if (link && link.linkEntity && link.userId && rowData) {
            const entity = getRBAC.find((e) => e.entityCode === link.linkEntity);
            if (entity && entity.isAccessible === EntityAccess.ALLOWED) {
                if (params.impersonationid && (entity.isGroupScope === EntityAccess.ALLOWED || (rowData[link.userId] && params.impersonationid === rowData[link.userId]))) {
                    response = true;
                } else if (entity.isGroupScope === EntityAccess.ALLOWED || (rowData[link.userId] && userData && userData.id === rowData[link.userId])) {
                    response = true;
                }
            }
        }
        return response;
    };

    const handleEventClick = (data, ifelse?) => {
        if (props.tableSettingView === TableView.MILEAGE_CLAIMED
            || props.tableSettingView === TableView.MILEAGE_APPROVED
            || props.tableSettingView === TableView.MILEAGE_REJECTED
            || props.tableSettingView === TableView.DRIVER_MILEAGE_CLAIM_LIST) {
            history.push(`/mileage-claim-details/${data?.userId}/${data?._id}`);
        } else {
            history.push(`/admin/eventdetails/${data?.userId}/${data?.eventId}/${data?.reportStatus}`);
        }
    }

    return (
        <>
            <CustomBox className={classes.customPraisoHeader}>
                {!isHideTableSelector &&
                    <PraisoTableViewSelector
                        changeTableView={changeTableView}
                        tableViewList={tableViewList}
                        handleRefreshComponent={handleRefreshComponent}
                        {...props}
                    />}
                {globalFilterEnabled ? (
                    <GlobalFilter
                        placeholder={globalFilterPlaceholder}
                        preGlobalFilteredRows={preGlobalFilteredRows}
                        globalFilter={state.globalFilter}
                        setGlobalFilter={setGlobalFilter}
                    />
                ) : null}
                {props.isShowGlobalDropDown ?
                    <GlobalDropDown

                    /> : null
                }
                {showMoreOrLessComp ? showMoreOrLessComp : null}
            </CustomBox>
            <CustomTable className={classes.table} {...getTableProps()}>
                <CustomTableHead>
                    {
                        // Loop over the header rows
                        headerGroups.map((headerGroup, index) => (
                            // Apply the header row props
                            <CustomTableRow
                                key={index}
                                className={classes.bgWhite}
                                {...headerGroup.getHeaderGroupProps()}
                            >
                                {
                                    // Loop over the headers in each row
                                    headerGroup.headers.map((column, index) => (
                                        // Apply the header cell props

                                        <CustomTableCell key={index}
                                            className={column.isTrendIcon ? clsx(classes.tableHeader, classes.cellTrend) : classes.tableHeader}
                                            {...column.getHeaderProps([
                                                {
                                                    style:
                                                        column.style && column.style.header
                                                            ? column.style.header
                                                            : null,
                                                },
                                            ])}>

                                            {column.canGroupBy ? (
                                                // If the column can be grouped, let's add a toggle
                                                <span
                                                    className={classes.groupByIcon}
                                                    {...column.getGroupByToggleProps()}
                                                >
                                                    <img
                                                        width={24}
                                                        src={
                                                            column.isGrouped ? groupeOnIcon : groupeOffIcon
                                                        }
                                                    />
                                                </span>
                                            ) : null}
                                            {
                                                // Render the header
                                                column.render("Header")
                                            }
                                            {column.canSort ? (
                                                <span {...column.getSortByToggleProps()}>
                                                    {column.isSorted ? (
                                                        column.isSortedDesc ? (
                                                            <CustomExpandMoreIcon
                                                                className={classes.sortArrow}
                                                            />
                                                        ) : (
                                                            <CustomExpandLessIcon
                                                                className={classes.sortArrow}
                                                            />
                                                        )
                                                    ) : (
                                                        <CustomUnfoldMoreIcon
                                                            className={classes.sortArrow}
                                                        />
                                                    )}
                                                </span>
                                            ) : null}

                                        </CustomTableCell>

                                    ))
                                }
                            </CustomTableRow>
                        ))
                    }
                </CustomTableHead>
                <CustomTableBody {...getTableBodyProps()}>
                    {props.loading ? (
                        <CustomTableRow className={classes.bgWhite}>
                            <CustomTableCell colSpan={visibleColumns.length}>
                                <CustomLoader />
                            </CustomTableCell>
                        </CustomTableRow>
                    ) : !props.loading && !tableData.length ? (
                        <CustomTableRow className={classes.bgWhite}>
                            <CustomTableCell colSpan={visibleColumns.length}>
                                <CustomTypography style={{ textAlign: "center" }}>
                                    {t("NO_RECORDS_FOUND")}
                                </CustomTypography>
                            </CustomTableCell>
                        </CustomTableRow>
                    ) : isAccordionView && !groupByEnabled ? (
                        page.map((row) => {
                            // Prepare the row for display
                            prepareRow(row);
                            return (
                                <React.Fragment key={row.id}>
                                    <CustomTableRow {...row.getRowProps()}
                                        id={`praisoTableRow_${row.id}`}
                                        className={(props.tableSettingView === TableView.APPROVED_EVENTS || props.tableSettingView === TableView.REJECTED_EVENTS) ? clsx(classes.bgWhite, classes.cellCursor) : clsx(classes.bgWhite)}
                                        onClick={(e) => (props.tableSettingView === TableView.APPROVED_EVENTS || props.tableSettingView === TableView.REJECTED_EVENTS) ? handleEventClick(row.original, "line1") : null}
                                    >
                                        {row.cells.map((cell, index) => {
                                            return (
                                                <CustomTableCell
                                                    key={index}
                                                    className={
                                                        cell.column &&
                                                            cell.column.linkTo &&
                                                            isClickable(
                                                                cell.column.linkTo,
                                                                row.original ? row.original : null
                                                            )
                                                            ? classes.cellCursor
                                                            : cell.column.isDate && (row.original.reportStatus === ReportedEventStatus.REPORTED || row.original.reportStatus === ReportedEventStatus.REJECTED) ? classes.cellReported : props.tableSettingView === TableView.REPORT_EVENTS ? classes.cellCursor : null
                                                    }

                                                    onClick={(e) => props.tableSettingView === TableView.REPORT_EVENTS && index !== row.cells.length - 1 ? handleEventClick(row.original, "line2") : null}
                                                    {...cell.getCellProps([
                                                        {
                                                            style:
                                                                cell &&
                                                                    cell.column &&
                                                                    cell.column.style &&
                                                                    cell.column.style.column
                                                                    ? cell.column.style.column
                                                                    : null,
                                                        },
                                                    ])}
                                                >
                                                    {cell.column.isDate && row?.original?.reportStatus === ReportedEventStatus.REJECTED ? <div className={classes.rejectEvent}>{t('Event Rejected')}</div> : null}
                                                    {cell.column.isDate && row?.original?.reportStatus === ReportedEventStatus.REPORTED ? <div className={classes.reportEvent}>{t('Event Reported')}</div> : null}

                                                    {cell.isAggregated
                                                        ? cell.render("Aggregated")
                                                        : cell.isPlaceholder
                                                            ? null
                                                            : cell.column &&
                                                                cell.column.linkTo && isClickable(
                                                                    cell.column.linkTo,
                                                                    row.original ? row.original : null
                                                                ) ? (
                                                                <Link to={params.impersonationid ? `/impersonation/${row.original[cell.column.linkTo.linkParam]}${cell.column.linkTo.linkUrl}/${row.original[cell.column.linkTo.linkParam]}${cell.column.linkTo.linkParamAppend ? cell.column.linkTo.linkParamAppend : ""}` : `${cell.column.linkTo.linkUrl}/${row.original[cell.column.linkTo.linkParam]}${cell.column.linkTo.linkParamAppend ? cell.column.linkTo.linkParamAppend : ""}`} className={classes.userLink}>{cell.render("Cell")}</Link>
                                                            ) : cell.render("Cell")}

                                                </CustomTableCell>
                                            );
                                        })}
                                    </CustomTableRow>
                                    {isRowExpanded || row.isExpanded ? (
                                        <CustomTableRow
                                            className={isAccordionView ? classes.expandedRow : null}
                                        >
                                            <CustomTableCell
                                                colSpan={visibleColumns.length}
                                                className={
                                                    isAccordionView ? classes.expandedRowCell : null
                                                }
                                            >
                                                {/*
                                                    Inside it, call our renderRowSubComponent function. In reality,
                                                    you could pass whatever you want as props to
                                                    a component like this, including the entire
                                                    table instance. But for this example, we'll just
                                                    pass the row
                                                */}
                                                {renderRowSubComponent(
                                                    { row },
                                                    tableAccordionProps.component,
                                                    tableAccordionProps.componentProps
                                                )}
                                            </CustomTableCell>
                                        </CustomTableRow>
                                    ) : null}
                                </React.Fragment>
                            );
                        })
                    ) : (
                        page.map((row, i) => {
                            // Prepare the row for display
                            prepareRow(row);

                            return (
                                <CustomTableRow key={row.id}
                                    id={`praisoTableRow_${row.id}`}
                                    className={(props.tableSettingView === TableView.APPROVED_EVENTS ||
                                        props.tableSettingView === TableView.BRAKING_APPROVED_EVENTS ||
                                        props.tableSettingView === TableView.CORNERING_APPROVED_EVENTS ||
                                        props.tableSettingView === TableView.ACCELERATION_APPROVED_EVENTS ||
                                        props.tableSettingView === TableView.DISTRACTION_APPROVED_EVENTS ||
                                        props.tableSettingView === TableView.REJECTED_EVENTS ||
                                        props.tableSettingView === TableView.BRAKING_REJECTED_EVENTS ||
                                        props.tableSettingView === TableView.CORNERING_REJECTED_EVENTS ||
                                        props.tableSettingView === TableView.ACCELERATION_REJECTED_EVENTS ||
                                        props.tableSettingView === TableView.DISTRACTION_REJECTED_EVENTS
                                        || props.tableSettingView === TableView.MILEAGE_CLAIMED
                                        || props.tableSettingView === TableView.MILEAGE_APPROVED
                                        || props.tableSettingView === TableView.MILEAGE_REJECTED) ? clsx(classes.bgWhite, classes.cellCursor) : clsx(classes.bgWhite)}
                                    onClick={(e) => (props.tableSettingView === TableView.APPROVED_EVENTS ||
                                        props.tableSettingView === TableView.BRAKING_APPROVED_EVENTS ||
                                        props.tableSettingView === TableView.CORNERING_APPROVED_EVENTS ||
                                        props.tableSettingView === TableView.ACCELERATION_APPROVED_EVENTS ||
                                        props.tableSettingView === TableView.DISTRACTION_APPROVED_EVENTS ||
                                        props.tableSettingView === TableView.REJECTED_EVENTS ||
                                        props.tableSettingView === TableView.BRAKING_REJECTED_EVENTS ||
                                        props.tableSettingView === TableView.CORNERING_REJECTED_EVENTS ||
                                        props.tableSettingView === TableView.ACCELERATION_REJECTED_EVENTS ||
                                        props.tableSettingView === TableView.DISTRACTION_REJECTED_EVENTS
                                        // || props.tableSettingView === TableView.MILEAGE_CLAIMED
                                        || props.tableSettingView === TableView.MILEAGE_APPROVED
                                        || props.tableSettingView === TableView.MILEAGE_REJECTED) ? row.original ? handleEventClick(row.original, "line3") : null : null  //!row.isGrouped ? handleEventClick(row.cells[i].column.filteredRows[i].original) : null : classes.cellCursor}
                                    }>
                                    {
                                        row.cells.map((cell, index) => {

                                            return (
                                                <CustomTableCell key={index}
                                                    {...cell.getCellProps([
                                                        {
                                                            style:
                                                                cell &&
                                                                    cell.column &&
                                                                    cell.column.style &&
                                                                    cell.column.style.column
                                                                    ? cell.column.style.column
                                                                    : null,
                                                        },
                                                    ])}
                                                    className={
                                                        cell.column &&
                                                            cell.column.linkTo &&
                                                            isClickable(
                                                                cell.column.linkTo,
                                                                row.original ? row.original : null
                                                            )
                                                            ? classes.cellCursor
                                                            : cell.column.isDate && (row?.original?.reportStatus === ReportedEventStatus.REPORTED || row?.original?.reportStatus === ReportedEventStatus.REJECTED) ? classes.cellReported : (props.tableSettingView === TableView.REPORT_EVENTS || props.tableSettingView === TableView.BRAKING_REPORTED_EVENTS || props.tableSettingView === TableView.CORNERING_REPORTED_EVENTS || props.tableSettingView === TableView.ACCELERATION_REPORTED_EVENTS || props.tableSettingView === TableView.DISTRACTION_REPORTED_EVENTS) ? classes.cellCursor : null
                                                    }

                                                    onClick={(e) => (props.tableSettingView === TableView.REPORT_EVENTS ||
                                                        props.tableSettingView === TableView.BRAKING_REPORTED_EVENTS ||
                                                        props.tableSettingView === TableView.CORNERING_REPORTED_EVENTS ||
                                                        props.tableSettingView === TableView.ACCELERATION_REPORTED_EVENTS ||
                                                        props.tableSettingView === TableView.DISTRACTION_REPORTED_EVENTS ||
                                                        props.tableSettingView === TableView.MILEAGE_CLAIMED ||
                                                        props.tableSettingView === TableView.MILEAGE_APPROVED ||
                                                        props.tableSettingView === TableView.MILEAGE_REJECTED ||
                                                        props.tableSettingView === TableView.DRIVER_MILEAGE_CLAIM_LIST
                                                    ) && index !== row.cells.length - 1 ? row?.canExpand ? (row?.original !== null && row?.original !== undefined) : !row?.isExpanded ? !row?.cells[index].column.id.includes("Action") ? handleEventClick(row.original, "abcd") : null : null : null}    // !row.isGrouped ? handleEventClick(row.cells[index].column.filteredRows[index].original) : null : null}
                                                >
                                                    {cell.column.isDate && row?.original?.reportStatus === ReportedEventStatus.REJECTED ? <div className={classes.rejectEvent}>{t('Event Rejected')}</div> : null}
                                                    {cell.column.isDate && row?.original?.reportStatus === ReportedEventStatus.REPORTED ? <div className={classes.reportEvent}>{t('Event Reported')}</div> : null}
                                                    {cell.isAggregated ? (
                                                        <div className={classes.aggregatedCell}>
                                                            {cell.render("Aggregated")}
                                                        </div>
                                                    ) : cell.isPlaceholder ? null : (
                                                        cell.column &&
                                                            cell.column.linkTo && isClickable(
                                                                cell.column.linkTo,
                                                                row.original ? row.original : null
                                                            ) ? (
                                                            <Link to={params.impersonationid ? `/impersonation/${row.original[cell.column.linkTo.linkParam]}${cell.column.linkTo.linkUrl}/${row.original[cell.column.linkTo.linkParam]}${cell.column.linkTo.linkParamAppend ? cell.column.linkTo.linkParamAppend : ""}` : `${cell.column.linkTo.linkUrl}/${row.original[cell.column.linkTo.linkParam]}${cell.column.linkTo.linkParamAppend ? cell.column.linkTo.linkParamAppend : ""}`} className={classes.userLink}>{cell.render("Cell")}</Link>
                                                        ) : cell.render("Cell")
                                                    )}
                                                </CustomTableCell>
                                            );
                                        })
                                    }
                                </CustomTableRow>
                            );
                        })
                    )}
                    {/* {} */}
                </CustomTableBody>
            </CustomTable >
            {
                showPagination ? <CustomBox className={classes.praisoTablePagination
                } >
                    <CustomBox>
                        Rows per page
                        <CustomSelect
                            disableUnderline
                            className={classes.lengthDropdown}
                            value={pageSize}
                            onChange={(e) => {
                                setPageSize(Number(e.target.value));
                            }}
                        >
                            {pageLengthArray.map((pageSize) => (
                                <CustomMenuItem key={pageSize} value={pageSize}>
                                    {pageSize}
                                </CustomMenuItem>
                            ))}
                        </CustomSelect>
                    </CustomBox>
                    <CustomBox className={classes.pageCounter}>
                        Page {pageIndex + 1} of total {pageOptions.length}
                    </CustomBox>
                    <CustomBox>
                        <CustomIconButton
                            onClick={() => previousPage()}
                            disabled={!canPreviousPage}
                            aria-label="Previous page"
                        >
                            <CustomChevronLeftIcon />
                        </CustomIconButton>
                        <CustomIconButton
                            onClick={() => nextPage()}
                            disabled={!canNextPage}
                            aria-label="Next page"
                        >
                            <CustomChevronRightIcon />
                        </CustomIconButton>
                    </CustomBox>
                </CustomBox > : null
            }
        </>
    );
};
